import { useState } from 'react';
import LegalModal from '../LegalModal';

/** Determine which extra contact field to show based on what the user already has */
function getMissingContact(user) {
  if (!user) return null;
  // User signed in with phone → needs email
  if (user.phone && !user.email) return 'email';
  // User signed in with email → needs phone
  if (user.email && !user.phone) return 'phone';
  return null;
}

export default function StepAccount({ data, update, onNext, onBack, user }) {
  const [errors, setErrors] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [showLegal, setShowLegal] = useState(null); // 'terms' | 'privacy' | null

  const missingContact = getMissingContact(user);

  function validate() {
    const e = {};
    if (!data.firstName?.trim()) e.firstName = 'First name is required';
    if (!data.lastName?.trim()) e.lastName = 'Last name is required';
    if (!acceptedTerms) e.terms = 'You must accept the Terms of Service';
    if (!acceptedPrivacy) e.privacy = 'You must accept the Privacy Policy';
    // Validate the extra contact field
    if (missingContact === 'phone') {
      const digits = (data.phone || '').replace(/\D/g, '');
      if (digits.length < 10) e.phone = 'Please enter a valid phone number';
    } else if (missingContact === 'email') {
      const email = (data.secondaryEmail || '').trim();
      if (!email.includes('@') || email.length < 5) e.secondaryEmail = 'Please enter a valid email address';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleNext() {
    if (validate()) onNext();
  }

  const hasContact = missingContact === 'phone'
    ? (data.phone || '').replace(/\D/g, '').length >= 10
    : missingContact === 'email'
      ? (data.secondaryEmail || '').includes('@')
      : true;
  const canProceed = data.firstName?.trim() && data.lastName?.trim() && acceptedTerms && acceptedPrivacy && hasContact;

  return (
    <div className="screen">
      {/* Back */}
      <button
        onClick={onBack}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', color: 'var(--text-secondary)',
          fontSize: '0.875rem', cursor: 'pointer', padding: 0, marginBottom: 8,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      <h1 className="screen-title" style={{ fontSize: '1.25rem', marginBottom: 4 }}>
        Create Your Account
      </h1>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginBottom: 28 }}>
        Your garage starts here
      </p>

      <div className="form-group">
        <label className="form-label">First Name</label>
        <input
          className="form-input"
          type="text"
          value={data.firstName || ''}
          onChange={(e) => { update({ firstName: e.target.value }); setErrors((p) => ({ ...p, firstName: undefined })); }}
          placeholder="First name"
          autoComplete="given-name"
        />
        {errors.firstName && <p style={{ color: 'var(--danger)', fontSize: '0.75rem', marginTop: 4 }}>{errors.firstName}</p>}
      </div>

      <div className="form-group">
        <label className="form-label">Last Name</label>
        <input
          className="form-input"
          type="text"
          value={data.lastName || ''}
          onChange={(e) => { update({ lastName: e.target.value }); setErrors((p) => ({ ...p, lastName: undefined })); }}
          placeholder="Last name"
          autoComplete="family-name"
        />
        {errors.lastName && <p style={{ color: 'var(--danger)', fontSize: '0.75rem', marginTop: 4 }}>{errors.lastName}</p>}
      </div>

      {/* Extra contact field — collect the missing email or phone */}
      {missingContact === 'phone' && (
        <div className="form-group">
          <label className="form-label">Phone Number</label>
          <input
            className="form-input"
            type="tel"
            inputMode="tel"
            value={data.phone || ''}
            onChange={(e) => { update({ phone: e.target.value }); setErrors((p) => ({ ...p, phone: undefined })); }}
            placeholder="(555) 555-1234"
            autoComplete="tel"
          />
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: 4 }}>
            So you can sign in with either your phone or email
          </p>
          {errors.phone && <p style={{ color: 'var(--danger)', fontSize: '0.75rem', marginTop: 4 }}>{errors.phone}</p>}
        </div>
      )}

      {missingContact === 'email' && (
        <div className="form-group">
          <label className="form-label">Email Address</label>
          <input
            className="form-input"
            type="email"
            inputMode="email"
            value={data.secondaryEmail || ''}
            onChange={(e) => { update({ secondaryEmail: e.target.value }); setErrors((p) => ({ ...p, secondaryEmail: undefined })); }}
            placeholder="you@email.com"
            autoComplete="email"
          />
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: 4 }}>
            So you can sign in with either your email or phone
          </p>
          {errors.secondaryEmail && <p style={{ color: 'var(--danger)', fontSize: '0.75rem', marginTop: 4 }}>{errors.secondaryEmail}</p>}
        </div>
      )}

      {/* Legal Acceptance */}
      <div style={{ marginTop: 8, marginBottom: 20 }}>
        {/* Terms of Service */}
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            marginBottom: 12,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => { setAcceptedTerms(e.target.checked); setErrors((p) => ({ ...p, terms: undefined })); }}
            style={{
              width: 18,
              height: 18,
              marginTop: 2,
              accentColor: 'var(--accent)',
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
            I agree to the{' '}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setShowLegal('terms'); }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent)',
                fontWeight: 600,
                fontSize: '0.8125rem',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              Terms of Service
            </button>
          </span>
        </label>
        {errors.terms && <p style={{ color: 'var(--danger)', fontSize: '0.75rem', marginTop: -8, marginBottom: 8, marginLeft: 28 }}>{errors.terms}</p>}

        {/* Privacy Policy */}
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={acceptedPrivacy}
            onChange={(e) => { setAcceptedPrivacy(e.target.checked); setErrors((p) => ({ ...p, privacy: undefined })); }}
            style={{
              width: 18,
              height: 18,
              marginTop: 2,
              accentColor: 'var(--accent)',
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
            I agree to the{' '}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setShowLegal('privacy'); }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent)',
                fontWeight: 600,
                fontSize: '0.8125rem',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              Privacy Policy
            </button>
          </span>
        </label>
        {errors.privacy && <p style={{ color: 'var(--danger)', fontSize: '0.75rem', marginTop: 4, marginLeft: 28 }}>{errors.privacy}</p>}
      </div>

      <div className="nav-row">
        <button className="btn btn-primary" disabled={!canProceed} onClick={handleNext}>
          Continue
        </button>
      </div>

      {/* Legal modals */}
      {showLegal && (
        <LegalModal
          type={showLegal}
          onClose={() => setShowLegal(null)}
          onAccept={() => {
            if (showLegal === 'terms') setAcceptedTerms(true);
            if (showLegal === 'privacy') setAcceptedPrivacy(true);
            setErrors((p) => ({ ...p, [showLegal === 'terms' ? 'terms' : 'privacy']: undefined }));
            setShowLegal(null);
          }}
        />
      )}
    </div>
  );
}
